import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  changeMeetingTypeStatus,
  createMeetingType,
  deleteMeetingType,
  getMeetingTypes,
  updateMeetingType,
} from '../api/meetingTypeApi'
import type { MeetingType, MeetingTypePage, MeetingTypeStatus, MeetingTypeUpsert } from '../types'

const KEY = 'meeting-types'

/**
 * Toplantı Türü yönetimi sunucu-durumu: liste + oluştur/güncelle/durum/sil. Her başarıda liste
 * invalidate edilir; güncel kayıt ayrıca cache'e yazılır (bayat `modDate` → sahte 409 önlenir).
 *
 * <p>Yeniden sıralama **yoktur** — tabloda `SORT_INDEX` kolonu yok (bkz. `types.ts`).
 */
export function useMeetingTypes(q?: string) {
  const { i18n } = useTranslation()
  const qc = useQueryClient()

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: [KEY] })
  }

  const applyUpdated = (updated: MeetingType) =>
    qc.setQueryData<MeetingTypePage>([KEY, i18n.language, q ?? ''], (prev) =>
      prev
        ? { ...prev, content: prev.content.map((t) => (t.id === updated.id ? updated : t)) }
        : prev,
    )

  const list = useQuery({
    queryKey: [KEY, i18n.language, q ?? ''],
    queryFn: () => getMeetingTypes(q),
  })

  // meta.handledCodes: yinelenen ad ekranda alan hatası olarak gösterilir → merkezi hata toast'ı susar.
  const create = useMutation({
    mutationFn: (body: MeetingTypeUpsert) => createMeetingType(body),
    onSuccess: invalidate,
    meta: { handledCodes: ['DUPLICATE_NAME'] },
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: MeetingTypeUpsert }) => updateMeetingType(id, body),
    onSuccess: (data) => {
      applyUpdated(data)
      invalidate()
    },
    meta: { handledCodes: ['DUPLICATE_NAME'] },
  })

  const changeStatus = useMutation({
    mutationFn: ({ id, body }: { id: number; body: MeetingTypeStatus }) =>
      changeMeetingTypeStatus(id, body),
    onSuccess: (data) => {
      applyUpdated(data)
      invalidate()
    },
  })

  const remove = useMutation({
    mutationFn: (id: number) => deleteMeetingType(id),
    onSuccess: invalidate,
  })

  return { list, create, update, changeStatus, remove }
}
